import { useState, useCallback, useRef, useEffect } from 'react';
import {
  Upload,
  FileText,
  Image,
  File,
  Trash2,
  Download,
  Loader2,
  AlertCircle,
  X,
  Pin,
  PinOff,
  Link2,
  Search,
} from 'lucide-react';
import { apiUpload, apiGet, apiPost, apiDelete, apiPatch, apiDownloadUrl } from '../lib/api';
import { useToast } from '../contexts/ToastContext';
import { AttachmentPreviewModal } from './AttachmentPreviewModal';

// ── Types ───────────────────────────────────────────────────────────

interface Attachment {
  id: string;
  filename: string;
  filepath: string;
  mime_type: string;
  size_bytes: number;
  meeting_instance_id: string | null;
  task_id: string | null;
  reference_doc_id: string | null;
  meeting_series_id: string | null;
  pinned_from_date: string | null;
  pinned_until_date: string | null;
  created_at: string;
  deleted_at: string | null;
}

interface FileUploadProps {
  parentType: 'meeting' | 'task' | 'reference_doc' | 'meeting_series' | 'note';
  parentId: string;
  onCountChange?: (count: number) => void;
  /** When present with parentType='meeting', enables pin/unpin UI */
  seriesId?: string;
  /** Meeting date for temporal pin logic */
  meetingDate?: string;
  /** When present, fetches series attachments filtered by date */
  asOfDate?: string;
  /** Hide upload drop zone (for read-only series attachment views) */
  readOnly?: boolean;
}

// ── Helpers ─────────────────────────────────────────────────────────

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = bytes / Math.pow(1024, i);
  return `${size.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function getFileIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) return Image;
  if (mimeType === 'application/pdf') return FileText;
  return File;
}

const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

function isPreviewable(mimeType: string): boolean {
  return mimeType.startsWith('image/') || mimeType === 'application/pdf' || mimeType === 'message/rfc822' || mimeType === DOCX_MIME;
}

function parentIdKey(parentType: FileUploadProps['parentType']): string {
  switch (parentType) {
    case 'meeting':
      return 'meeting_instance_id';
    case 'task':
      return 'task_id';
    case 'reference_doc':
      return 'reference_doc_id';
    case 'meeting_series':
      return 'meeting_series_id';
    case 'note':
      return 'note_id';
  }
}

// ── Component ───────────────────────────────────────────────────────

export function FileUpload({
  parentType,
  parentId,
  onCountChange,
  seriesId,
  meetingDate,
  asOfDate,
  readOnly,
}: FileUploadProps) {
  const toast = useToast();
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [linkedFiles, setLinkedFiles] = useState<(Attachment & { ref_id: string })[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState<Attachment | null>(null);
  const [unpinDropdownId, setUnpinDropdownId] = useState<string | null>(null);
  const [linkPickerOpen, setLinkPickerOpen] = useState(false);
  const [linkQuery, setLinkQuery] = useState('');
  const [linkResults, setLinkResults] = useState<{ id: string; filename: string }[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const unpinRef = useRef<HTMLDivElement>(null);
  const linkRef = useRef<HTMLDivElement>(null);
  const linkInputRef = useRef<HTMLInputElement>(null);

  // Close unpin dropdown on outside click
  useEffect(() => {
    if (!unpinDropdownId) return;
    function handleClick(e: MouseEvent) {
      if (unpinRef.current && !unpinRef.current.contains(e.target as Node)) {
        setUnpinDropdownId(null);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [unpinDropdownId]);

  // Load attachments when parentId changes
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const key = parentIdKey(parentType);
        let url = `/attachments?${key}=${parentId}`;
        if (parentType === 'meeting_series' && asOfDate) {
          url += `&as_of_date=${asOfDate}`;
        }
        const data = await apiGet<Attachment[]>(url);
        if (!cancelled) setAttachments(data);
      } catch (err) {
        console.error('Failed to load attachments:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [parentType, parentId, asOfDate]);

  // Load linked file references
  const entityTypeForRef = parentType === 'meeting' ? 'meeting_instance' : parentType;
  useEffect(() => {
    apiGet<(Attachment & { ref_id: string })[]>(
      `/attachments/references?entity_type=${entityTypeForRef}&entity_id=${parentId}`
    ).then(setLinkedFiles).catch(() => {});
  }, [entityTypeForRef, parentId]);

  // Notify parent of attachment count changes (include linked)
  useEffect(() => {
    onCountChange?.(attachments.length + linkedFiles.length);
  }, [attachments.length, linkedFiles.length, onCountChange]);

  // Link picker: close on outside click
  useEffect(() => {
    if (!linkPickerOpen) return;
    function handleClick(e: MouseEvent) {
      if (linkRef.current && !linkRef.current.contains(e.target as Node)) setLinkPickerOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    setTimeout(() => linkInputRef.current?.focus(), 50);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [linkPickerOpen]);

  // Link picker: search files
  useEffect(() => {
    if (!linkQuery.trim() || !linkPickerOpen) { setLinkResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const data = await apiGet<{ id: string; filename: string }[]>(
          `/attachments/search?q=${encodeURIComponent(linkQuery.trim())}&limit=8`
        );
        // Exclude files already attached or linked here
        const existingIds = new Set([...attachments.map(a => a.id), ...linkedFiles.map(l => l.id)]);
        setLinkResults(data.filter(f => !existingIds.has(f.id)));
      } catch { setLinkResults([]); }
    }, 200);
    return () => clearTimeout(timer);
  }, [linkQuery, linkPickerOpen, attachments, linkedFiles]);

  async function handleLinkFile(fileId: string) {
    try {
      await apiPost(`/attachments/${fileId}/reference`, {
        entity_type: entityTypeForRef,
        entity_id: parentId,
      });
      // Refresh linked files
      const updated = await apiGet<(Attachment & { ref_id: string })[]>(
        `/attachments/references?entity_type=${entityTypeForRef}&entity_id=${parentId}`
      );
      setLinkedFiles(updated);
      setLinkPickerOpen(false);
      setLinkQuery('');
      toast.success('File linked');
    } catch {
      toast.error('Failed to link file');
    }
  }

  async function handleUnlinkFile(refId: string) {
    try {
      await apiDelete(`/attachments/references/${refId}`);
      setLinkedFiles(prev => prev.filter(l => l.ref_id !== refId));
      toast.success('File unlinked');
    } catch {
      toast.error('Failed to unlink file');
    }
  }

  const uploadFile = useCallback(
    async (file: globalThis.File) => {
      setError(null);
      setUploading(true);

      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append(parentIdKey(parentType), parentId);

        const attachment = await apiUpload<Attachment>('/attachments', formData);
        setAttachments((prev) => [attachment, ...prev]);
        toast.success(`Uploaded ${file.name}`);
      } catch (err: any) {
        const msg = err.message || 'Upload failed';
        setError(msg);
        toast.error(msg);
      } finally {
        setUploading(false);
      }
    },
    [parentType, parentId, toast],
  );

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;
      const fileArray = Array.from(files);
      (async () => {
        for (const file of fileArray) {
          await uploadFile(file);
        }
      })();
    },
    [uploadFile],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleDownload = useCallback(async (attachment: Attachment) => {
    try {
      const blobUrl = await apiDownloadUrl(`/attachments/${attachment.id}/download`);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = attachment.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
    } catch (err) {
      console.error('Download failed:', err);
    }
  }, []);

  const handleDelete = useCallback(async (id: string, filename: string) => {
    if (!window.confirm(`Delete "${filename}"?`)) return;
    try {
      await apiDelete(`/attachments/${id}`);
      setAttachments((prev) => prev.filter((a) => a.id !== id));
      toast.success('Attachment deleted');
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error('Failed to delete attachment');
    }
  }, [toast]);

  const handlePin = useCallback(async (attachment: Attachment) => {
    if (!meetingDate) return;
    try {
      const updated = await apiPatch<Attachment>(`/attachments/${attachment.id}/pin`, {
        from_date: meetingDate,
      });
      setAttachments((prev) => prev.map((a) => a.id === updated.id ? updated : a));
      toast.success('Pinned to series');
    } catch (err) {
      console.error('Pin failed:', err);
      toast.error('Failed to pin attachment');
    }
  }, [meetingDate, toast]);

  const handleUnpin = useCallback(async (attachment: Attachment, mode: 'immediate' | 'future') => {
    if (!meetingDate) return;
    setUnpinDropdownId(null);
    try {
      const updated = await apiPatch<Attachment>(`/attachments/${attachment.id}/unpin`, {
        mode,
        meeting_date: meetingDate,
      });
      setAttachments((prev) => prev.map((a) => a.id === updated.id ? updated : a));
      toast.success(mode === 'immediate' ? 'Unpinned from current & future' : 'Unpinned from future meetings');
    } catch (err) {
      console.error('Unpin failed:', err);
      toast.error('Failed to unpin attachment');
    }
  }, [meetingDate, toast]);

  const canPin = parentType === 'meeting' && !!seriesId && !!meetingDate;
  const canUnpin = !!meetingDate;

  return (
    <div
      className={`space-y-3 rounded-lg transition-all duration-200 ${
        dragOver ? 'ring-2 ring-accent bg-accent/5' : ''
      }`}
      onDrop={readOnly ? undefined : handleDrop}
      onDragOver={readOnly ? undefined : handleDragOver}
      onDragLeave={readOnly ? undefined : handleDragLeave}
    >
      {/* Drop zone */}
      {!readOnly && (
        <div
          onClick={() => fileInputRef.current?.click()}
          className={`
            relative flex flex-col items-center justify-center gap-2
            px-4 py-6 rounded-lg border-2 border-dashed cursor-pointer
            transition-all duration-200
            ${
              dragOver
                ? 'border-accent bg-accent/10 scale-[1.01]'
                : 'border-border hover:border-border-hover hover:bg-bg-hover/50'
            }
            ${uploading ? 'pointer-events-none opacity-60' : ''}
          `}
        >
          {uploading ? (
            <Loader2 size={24} className="text-accent animate-spin" />
          ) : (
            <Upload size={24} className="text-text-muted" />
          )}
          <div className="text-center">
            <p className="text-sm text-text-secondary">
              {uploading
                ? 'Uploading...'
                : dragOver
                  ? 'Drop files here'
                  : 'Drop files here or click to browse'}
            </p>
            <p className="text-xs text-text-muted mt-1">
              Max 50MB per file. Images, PDFs, documents, spreadsheets, archives.
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              handleFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </div>
      )}

      {/* Link existing file button */}
      {!readOnly && (
        <div ref={linkRef} className="relative">
          <button
            onClick={() => setLinkPickerOpen(!linkPickerOpen)}
            className="flex items-center gap-1.5 text-xs text-text-muted hover:text-accent transition-colors"
          >
            <Link2 size={13} /> Link existing file
          </button>
          {linkPickerOpen && (
            <div className="absolute top-6 left-0 z-30 w-64 bg-bg-surface border border-border rounded-lg shadow-xl overflow-hidden">
              <div className="p-2 flex items-center gap-2 border-b border-border">
                <Search size={13} className="text-text-muted flex-shrink-0" />
                <input
                  ref={linkInputRef}
                  type="text"
                  value={linkQuery}
                  onChange={e => setLinkQuery(e.target.value)}
                  placeholder="Search files..."
                  className="flex-1 bg-transparent text-sm text-text-primary placeholder:text-text-muted focus:outline-none"
                />
              </div>
              {linkResults.length > 0 ? (
                <div className="max-h-48 overflow-y-auto">
                  {linkResults.map(f => (
                    <button
                      key={f.id}
                      onClick={() => handleLinkFile(f.id)}
                      className="w-full text-left px-3 py-2 text-sm text-text-secondary hover:bg-bg-hover hover:text-text-primary transition-colors truncate"
                    >
                      {f.filename}
                    </button>
                  ))}
                </div>
              ) : linkQuery.trim() ? (
                <div className="px-3 py-2 text-xs text-text-muted">No files found</div>
              ) : (
                <div className="px-3 py-2 text-xs text-text-muted">Type to search files</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-danger/10 border border-danger/30">
          <AlertCircle size={14} className="text-danger shrink-0" />
          <span className="text-sm text-danger flex-1">{error}</span>
          <button
            onClick={() => setError(null)}
            className="text-danger/60 hover:text-danger transition-colors"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* File list */}
      {loading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 size={18} className="text-text-muted animate-spin" />
        </div>
      ) : attachments.length > 0 ? (
        <ul className="space-y-1">
          {attachments.map((attachment) => {
            const Icon = getFileIcon(attachment.mime_type);
            const isPinned = !!attachment.pinned_from_date;
            const previewable = isPreviewable(attachment.mime_type);
            return (
              <li
                key={attachment.id}
                onClick={() => {
                  if (previewable) setPreviewAttachment(attachment);
                  else handleDownload(attachment);
                }}
                className="flex items-center gap-2.5 px-3 py-2 rounded-md bg-bg-surface hover:bg-bg-hover/50 transition-colors group cursor-pointer"
              >
                <Icon size={16} className="text-text-muted shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm text-text-primary truncate">
                      {attachment.filename}
                    </p>
                    {isPinned && (
                      <Pin size={10} className="text-accent shrink-0" />
                    )}
                  </div>
                  <p className="text-xs text-text-muted">
                    {formatFileSize(attachment.size_bytes)}
                  </p>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {/* Unpin button */}
                  {isPinned && canUnpin && (
                    <div className="relative" ref={unpinDropdownId === attachment.id ? unpinRef : undefined}>
                      <button
                        onClick={(e) => { e.stopPropagation(); setUnpinDropdownId(unpinDropdownId === attachment.id ? null : attachment.id); }}
                        title="Unpin from series"
                        className="p-1 rounded text-accent hover:text-accent-hover hover:bg-bg-hover transition-colors"
                      >
                        <PinOff size={14} />
                      </button>
                      {unpinDropdownId === attachment.id && (
                        <div className="absolute right-0 top-full mt-1 z-30 bg-bg-surface border border-border rounded-lg shadow-lg py-1 w-52">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleUnpin(attachment, 'immediate'); }}
                            className="w-full text-left px-3 py-1.5 text-xs text-text-primary hover:bg-bg-hover transition-colors"
                          >
                            Remove immediately
                            <span className="block text-[10px] text-text-muted mt-0.5">Gone from current + future meetings</span>
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleUnpin(attachment, 'future'); }}
                            className="w-full text-left px-3 py-1.5 text-xs text-text-primary hover:bg-bg-hover transition-colors"
                          >
                            Remove from future only
                            <span className="block text-[10px] text-text-muted mt-0.5">Keeps through this meeting</span>
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  {/* Pin button for unpinned instance attachments */}
                  {canPin && attachment.meeting_instance_id && !isPinned && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handlePin(attachment); }}
                      title="Pin to series"
                      className="p-1 rounded text-text-muted hover:text-accent hover:bg-bg-hover transition-colors"
                    >
                      <Pin size={14} />
                    </button>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDownload(attachment); }}
                    title="Download"
                    className="p-1 rounded text-text-muted hover:text-accent hover:bg-bg-hover transition-colors"
                  >
                    <Download size={14} />
                  </button>
                  {!readOnly && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(attachment.id, attachment.filename); }}
                      title="Delete"
                      className="p-1 rounded text-text-muted hover:text-danger hover:bg-bg-hover transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}

      {/* Linked file references */}
      {linkedFiles.length > 0 && (
        <ul className="space-y-1">
          {linkedFiles.map((lf) => {
            const isDeleted = !!lf.deleted_at;
            const Icon = getFileIcon(lf.mime_type);
            const previewable = !isDeleted && isPreviewable(lf.mime_type);
            return (
              <li
                key={`ref-${lf.ref_id}`}
                className={`flex items-center gap-3 px-3 py-2 bg-bg-card border border-border rounded-md group ${isDeleted ? 'opacity-60' : ''}`}
              >
                <Link2 size={14} className={isDeleted ? 'text-text-muted flex-shrink-0' : 'text-accent flex-shrink-0'} />
                <Icon size={16} className="text-text-muted flex-shrink-0" />
                {isDeleted ? (
                  <span className="flex-1 text-sm text-text-muted truncate line-through">
                    {lf.filename} <span className="text-[10px] no-underline">(deleted)</span>
                  </span>
                ) : (
                  <button
                    onClick={() => previewable ? setPreviewAttachment(lf) : handleDownload(lf)}
                    className="flex-1 text-left text-sm text-text-primary hover:text-accent truncate transition-colors"
                  >
                    {lf.filename}
                  </button>
                )}
                {!isDeleted && (
                  <span className="text-[10px] text-text-muted flex-shrink-0">
                    {formatFileSize(lf.size_bytes)}
                  </span>
                )}
                {!isDeleted && (
                  <button
                    onClick={() => handleDownload(lf)}
                    className="p-1 text-text-muted hover:text-text-primary transition-colors opacity-0 group-hover:opacity-100"
                    title="Download"
                  >
                    <Download size={13} />
                  </button>
                )}
                {!readOnly && (
                  <button
                    onClick={() => handleUnlinkFile(lf.ref_id)}
                    className="p-1 text-text-muted hover:text-danger transition-colors opacity-0 group-hover:opacity-100"
                    title={isDeleted ? 'Remove' : 'Unlink'}
                  >
                    <X size={13} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Preview modal */}
      {previewAttachment && (
        <AttachmentPreviewModal
          attachment={previewAttachment}
          onClose={() => setPreviewAttachment(null)}
          canPin={canPin && !!previewAttachment.meeting_instance_id && !previewAttachment.pinned_from_date}
          canUnpin={canUnpin}
          isPinned={!!previewAttachment.pinned_from_date}
          onPin={() => {
            handlePin(previewAttachment);
            setPreviewAttachment(null);
          }}
          onUnpin={(mode) => {
            handleUnpin(previewAttachment, mode);
            setPreviewAttachment(null);
          }}
        />
      )}
    </div>
  );
}
